import React, { useState } from 'react';
import {
  X, Mail, Phone, Users, Briefcase, MapPin, User, Calendar, Clock,
  CheckCircle, BookOpen, Target, Download, Shield, Key, Settings, Info
} from 'lucide-react';
import './UserDetailModal.css';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  jobTitle: string;
  department: string;
  initials: string;
  progress: number;
  phone?: string;
  location?: string;
  manager?: string;
  joinDate: string;
  lastLogin: string;
  completedCourses: number;
  inProgressCourses: number;
}

interface Course {
  id: string;
  name: string;
  duration: string;
  lessons: number;
}

interface LearningHistoryItem {
  id: string;
  name: string;
  type: string;
  status: string;
  progress: number;
  score: string;
  enrolled: string;
  completed: string;
  certificate: boolean;
}

interface AdminActivity {
  action: string;
  date: string;
}

interface UserDetailModalProps {
  user: TeamMember;
  courses: Course[];
  learningHistory: LearningHistoryItem[];
  adminActivities: AdminActivity[];
  onClose: () => void;
  onEnrollCourse: (courseName: string) => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  user,
  courses,
  learningHistory,
  adminActivities,
  onClose,
  onEnrollCourse
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'enrollment' | 'admin'>('details');

  return (
    <div className="admin-user-modal-overlay">
      <div className="admin-user-detail-modal">
        <div className="admin-user-detail-header">
          <div className="admin-user-detail-info">
            <div className="admin-user-avatar admin-user-avatar-large">{user.initials}</div>
            <div>
              <h2 className="admin-user-detail-name">{user.name}</h2>
              <p className="admin-user-detail-title">{user.jobTitle}</p>
            </div>
            <span className="admin-user-status-badge-active">Active</span>
          </div>
          <button className="admin-user-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="admin-user-detail-tabs">
          <button
            className={`admin-user-detail-tab ${activeTab === 'details' ? 'admin-user-detail-tab-active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            User Details
          </button>
          <button
            className={`admin-user-detail-tab ${activeTab === 'history' ? 'admin-user-detail-tab-active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Learning History
          </button>
          <button
            className={`admin-user-detail-tab ${activeTab === 'enrollment' ? 'admin-user-detail-tab-active' : ''}`}
            onClick={() => setActiveTab('enrollment')}
          >
            Manual Enrollment
          </button>
          <button
            className={`admin-user-detail-tab ${activeTab === 'admin' ? 'admin-user-detail-tab-active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin Actions
          </button>
        </div>

        <div className="admin-user-detail-content">
          {activeTab === 'details' && (
            <div className="admin-user-details-tab">
              <section className="admin-user-detail-section">
                <h3 className="admin-user-detail-section-title">Personal Information</h3>
                <div className="admin-user-info-grid">
                  <div className="admin-user-info-item">
                    <div className="admin-user-info-label">EMAIL</div>
                    <div className="admin-user-info-value">
                      <Mail size={16} /> {user.email}
                    </div>
                  </div>
                  <div className="admin-user-info-item">
                    <div className="admin-user-info-label">PHONE</div>
                    <div className="admin-user-info-value">
                      <Phone size={16} /> {user.phone}
                    </div>
                  </div>
                  <div className="admin-user-info-item">
                    <div className="admin-user-info-label">DEPARTMENT</div>
                    <div className="admin-user-info-value">
                      <Users size={16} /> {user.department}
                    </div>
                  </div>
                  <div className="admin-user-info-item">
                    <div className="admin-user-info-label">JOB TITLE</div>
                    <div className="admin-user-info-value">
                      <Briefcase size={16} /> {user.jobTitle}
                    </div>
                  </div>
                  <div className="admin-user-info-item">
                    <div className="admin-user-info-label">LOCATION</div>
                    <div className="admin-user-info-value">
                      <MapPin size={16} /> {user.location}
                    </div>
                  </div>
                  <div className="admin-user-info-item">
                    <div className="admin-user-info-label">MANAGER</div>
                    <div className="admin-user-info-value">
                      <User size={16} /> {user.manager}
                    </div>
                  </div>
                  <div className="admin-user-info-item">
                    <div className="admin-user-info-label">JOIN DATE</div>
                    <div className="admin-user-info-value">
                      <Calendar size={16} /> {user.joinDate}
                    </div>
                  </div>
                  <div className="admin-user-info-item">
                    <div className="admin-user-info-label">LAST LOGIN</div>
                    <div className="admin-user-info-value">
                      <Clock size={16} /> {user.lastLogin}
                    </div>
                  </div>
                </div>
              </section>

              <section className="admin-user-detail-section">
                <h3 className="admin-user-detail-section-title">Learning Statistics</h3>
                <div className="admin-user-stats-grid">
                  <div className="admin-user-stat-box">
                    <div className="admin-user-stat-box-label">Completed</div>
                    <div className="admin-user-stat-box-value">{user.completedCourses}</div>
                    <div className="admin-user-stat-box-icon">
                      <CheckCircle size={32} />
                    </div>
                  </div>
                  <div className="admin-user-stat-box">
                    <div className="admin-user-stat-box-label">In Progress</div>
                    <div className="admin-user-stat-box-value">{user.inProgressCourses}</div>
                    <div className="admin-user-stat-box-icon">
                      <BookOpen size={32} />
                    </div>
                  </div>
                  <div className="admin-user-stat-box">
                    <div className="admin-user-stat-box-label">Completion Rate</div>
                    <div className="admin-user-stat-box-value">{user.progress}%</div>
                    <div className="admin-user-stat-box-icon">
                      <Target size={32} />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="admin-user-history-tab">
              <div className="admin-user-history-tab-header">
                <h3 className="admin-user-detail-section-title">Learning History</h3>
                <button className="admin-user-export-btn">
                  <Download size={16} /> Export Transcript
                </button>
              </div>

              <div className="admin-user-table-container">
                <table className="admin-user-table">
                  <thead>
                    <tr>
                      <th>COURSE</th>
                      <th>TYPE</th>
                      <th>STATUS</th>
                      <th>PROGRESS</th>
                      <th>SCORE</th>
                      <th>COMPLETED</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learningHistory.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="admin-user-course-info">
                            <div className="admin-user-course-icon">
                              <BookOpen size={20} />
                            </div>
                            <span>{item.name}</span>
                          </div>
                        </td>
                        <td>{item.type}</td>
                        <td>
                          <span className={`admin-user-status-badge ${
                            item.status === 'Completed' ? 'admin-user-status-completed' : 'admin-user-status-progress'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td>
                          <div className="admin-user-progress-cell">
                            <span>{item.progress}%</span>
                            <div className="admin-user-progress-bar">
                              <div
                                className="admin-user-progress-fill"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td>{item.score}</td>
                        <td>{item.completed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'enrollment' && (
            <div className="admin-user-enrollment-tab">
              <div className="admin-user-enrollment-header">
                <h3 className="admin-user-detail-section-title">Manual Enrollment</h3>
                <p className="admin-user-enrollment-subtitle">Enroll {user.name} in courses, paths, or certifications</p>
              </div>

              <input
                type="text"
                className="admin-user-search-input"
                placeholder="Search learning items..."
              />

              <div className="admin-user-enrollment-list">
                {courses.map(course => (
                  <div key={course.id} className="admin-user-enrollment-item">
                    <div className="admin-user-enrollment-item-info">
                      <div className="admin-user-course-icon">
                        <BookOpen size={20} />
                      </div>
                      <div>
                        <div className="admin-user-enrollment-item-name">{course.name}</div>
                        <div className="admin-user-enrollment-item-meta">
                          Course • {course.duration} • {course.lessons} modules
                        </div>
                      </div>
                    </div>
                    <button
                      className="admin-user-enroll-btn"
                      onClick={() => onEnrollCourse(course.name)}
                    >
                      + Enroll
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'admin' && (
            <div className="admin-user-admin-tab">
              <div className="admin-user-admin-header">
                <h3 className="admin-user-detail-section-title">Administrative Actions</h3>
                <p className="admin-user-enrollment-subtitle">Perform administrative tasks for this user</p>
              </div>

              <div className="admin-user-current-status">
                <div className="admin-user-status-label">CURRENT STATUS</div>
                <div className="admin-user-status-info">
                  <div className="admin-user-status-icon">
                    <Shield size={24} />
                  </div>
                  <div>
                    <span className="admin-user-status-badge-active">Active</span>
                    <span className="admin-user-status-last-login"> • Last login: {user.lastLogin}</span>
                  </div>
                </div>
              </div>

              <div className="admin-user-admin-actions-grid">
                <div className="admin-user-admin-action-card">
                  <div className="admin-user-admin-action-icon admin-user-icon-blue">
                    <Shield size={28} />
                  </div>
                  <h4 className="admin-user-admin-action-title">Update Status</h4>
                  <p className="admin-user-admin-action-desc">Change account status</p>
                  <button className="admin-user-admin-action-btn admin-user-btn-blue">Change Status</button>
                </div>

                <div className="admin-user-admin-action-card">
                  <div className="admin-user-admin-action-icon admin-user-icon-purple">
                    <Key size={28} />
                  </div>
                  <h4 className="admin-user-admin-action-title">Reset Password</h4>
                  <p className="admin-user-admin-action-desc">Send reset link</p>
                  <button className="admin-user-admin-action-btn admin-user-btn-purple">Send Reset Link</button>
                </div>

                <div className="admin-user-admin-action-card">
                  <div className="admin-user-admin-action-icon admin-user-icon-yellow">
                    <Settings size={28} />
                  </div>
                  <h4 className="admin-user-admin-action-title">Change Role</h4>
                  <p className="admin-user-admin-action-desc">Update permissions</p>
                  <button className="admin-user-admin-action-btn admin-user-btn-yellow">Update Role</button>
                </div>
              </div>

              <div className="admin-user-guidelines-box">
                <div className="admin-user-guidelines-header">
                  <Info size={18} className="admin-user-guidelines-icon" />
                  <span className="admin-user-guidelines-title">Status Change Guidelines</span>
                </div>
                <div className="admin-user-guidelines-content">
                  <div className="admin-user-guideline-item">
                    <strong>Active:</strong> User has full access to the platform
                  </div>
                  <div className="admin-user-guideline-item">
                    <strong>Inactive:</strong> Temporary access suspension (can be reactivated)
                  </div>
                  <div className="admin-user-guideline-item">
                    <strong>Suspended:</strong> Account under review or policy violation
                  </div>
                  <div className="admin-user-guideline-item">
                    <strong>Terminated:</strong> Employment ended, permanent deactivation
                  </div>
                  <div className="admin-user-guideline-item">
                    <strong>Resigned:</strong> User voluntarily left the organization
                  </div>
                </div>
              </div>

              <div className="admin-user-activity-section">
                <h4 className="admin-user-activity-title">Recent Admin Activity</h4>
                <div className="admin-user-activity-list">
                  {adminActivities.map((activity, index) => (
                    <div key={index} className="admin-user-activity-item">
                      <div className="admin-user-activity-icon">
                        <Clock size={16} />
                      </div>
                      <div>
                        <div className="admin-user-activity-action">{activity.action}</div>
                        <div className="admin-user-activity-date">{activity.date}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
